import React, { useMemo } from "react";
import { i18n } from "../../translate/i18n";
import ReportTable from "./ReportTable";
import { formatDuration, formatRate, percent } from "./format";

/**
 * Pestaña «Equipo» (doc 0447 §5): rendimiento por agente y sus transferencias.
 * Las transferencias cuentan desde que se activó el registro de eventos (D1).
 * La valoración (D5) se cruza por agente desde la encuesta de satisfacción.
 */
const TeamTab = ({ data }) => {
  const rows = useMemo(() => {
    const byUser = new Map();
    (data.byAgent || []).forEach((a) => {
      byUser.set(a.id === null ? "none" : String(a.id), { ...a, key: a.id ?? "none", sent: 0, received: 0, returned: 0 });
    });
    (data.transfers || []).forEach((t) => {
      const k = String(t.userId);
      const row = byUser.get(k) || {
        key: k,
        id: k,
        label: t.userName,
        conversations: 0,
        resolved: 0,
        avgWaitSeconds: null,
        avgResolutionSeconds: null,
        avgFirstResponseSeconds: null,
      };
      byUser.set(k, { ...row, sent: t.sent, received: t.received, returned: t.returned });
    });
    const rating = new Map((data.csatAgents || []).map((c) => [c.userId === null ? "none" : String(c.userId), c]));
    return [...byUser.values()].map((r) => {
      const c = rating.get(String(r.key));
      return {
        ...r,
        label: r.id === null || r.key === "none" ? i18n.t("reports.team.unassigned") : r.label,
        avgRate: c ? c.avgRate : null,
        rated: c ? c.answered : 0,
      };
    });
  }, [data.byAgent, data.transfers, data.csatAgents]);

  const columns = [
    { key: "label", label: i18n.t("reports.team.agent"), sort: (r) => String(r.label || "") },
    { key: "conversations", label: i18n.t("reports.kpi.conversations"), align: "right" },
    { key: "resolved", label: i18n.t("reports.kpi.resolved"), align: "right" },
    {
      key: "rate",
      label: i18n.t("reports.team.rate"),
      align: "right",
      sort: (r) => percent(r.resolved, r.conversations),
      render: (r) => `${percent(r.resolved, r.conversations)}%`,
    },
    {
      key: "avgFirstResponseSeconds",
      label: i18n.t("reports.kpi.avgFirstResponse"),
      align: "right",
      render: (r) => formatDuration(r.avgFirstResponseSeconds),
    },
    { key: "avgWaitSeconds", label: i18n.t("reports.kpi.avgWait"), align: "right", render: (r) => formatDuration(r.avgWaitSeconds) },
    {
      key: "avgResolutionSeconds",
      label: i18n.t("reports.kpi.avgResolution"),
      align: "right",
      render: (r) => formatDuration(r.avgResolutionSeconds),
    },
    { key: "sent", label: i18n.t("reports.team.sent"), align: "right" },
    { key: "received", label: i18n.t("reports.team.received"), align: "right" },
    { key: "returned", label: i18n.t("reports.team.returned"), align: "right" },
    {
      key: "avgRate",
      label: i18n.t("reports.csat.short"),
      align: "right",
      sort: (r) => r.avgRate ?? -1,
      render: (r) => (r.rated ? `${formatRate(r.avgRate)} (${r.rated})` : "—"),
    },
  ];

  return (
    <>
      <ReportTable title={i18n.t("reports.team.title")} columns={columns} rows={rows} defaultSort="conversations" rowKey="key" />
      <p style={{ fontSize: 12, opacity: 0.75, margin: "8px 4px 0" }}>{i18n.t("reports.team.transfersNote")}</p>
    </>
  );
};

export default TeamTab;
